import { useState } from "react";
import api, { formatError, pollProject } from "@/lib/api";
import { toast } from "sonner";

export default function RevisionsView({ project, onUpdate }) {
  const [notes, setNotes] = useState("");
  const [manuscript, setManuscript] = useState("");
  const [filename, setFilename] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [paying, setPaying] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setManuscript(data.text);
      setFilename(data.filename);
      toast.success("Versión corregida cargada.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const applyRevision = async () => {
    if (manuscript.trim().length < 20) { toast.error("Sube o pega tu versión corregida para revisar."); return; }
    setApplying(true);
    try {
      await api.post(`/projects/${project.id}/revisions`, { notes, manuscript_text: manuscript, manuscript_filename: filename });
      const data = await pollProject(project.id, (p) => !p.processing);
      onUpdate(data);
      if (data.process_error) {
        toast.error(data.process_error);
      } else {
        setNotes(""); setManuscript(""); setFilename(null);
        toast.success("Revisión aplicada. Mira el nuevo informe.");
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const startPayment = async () => {
    setPaying(true);
    try {
      localStorage.setItem("jarvis_project_id", project.id);
      const { data } = await api.post("/payments/checkout", { project_id: project.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setPaying(false); }
  };

  const rc = project.revision_count || 0;

  return (
    <div className="panel">
      <div className="split">
        <div className="stack">
          <span className="side-label">Nueva revisión</span>
          <label className="drop-zone">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1C3B73" strokeWidth="1.6"><path d="M12 16V4M6 10l6-6 6 6" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></svg>
            <strong>{uploading ? "Cargando…" : "Arrastra tu versión corregida"}</strong>
            <small style={{ color: "var(--muted)" }}>.docx, .pdf o .txt · o pega el texto abajo</small>
            <input type="file" accept=".txt,.md,.pdf,.docx" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          {filename && <div className="file"><span style={{ flex: 1, minWidth: 0 }}><strong style={{ display: "block" }}>{filename}</strong><small style={{ color: "var(--muted)" }}>Listo para aplicar</small></span></div>}
          <label className="field"><span>Texto corregido</span>
            <textarea rows={6} value={manuscript} onChange={(e) => setManuscript(e.target.value)} placeholder="…o pega aquí tu versión corregida." />
          </label>
          <label className="field"><span>Notas para esta revisión <small style={{ color: "var(--muted)", fontWeight: 400 }}>· opcional</small></span>
            <textarea data-testid="revision-notes-textarea" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: acortar el capítulo 2, tono más cercano." />
          </label>
          <button type="button" data-testid="apply-revision-btn" className="btn btn-ghost" style={{ alignSelf: "flex-start" }} onClick={applyRevision} disabled={applying}>
            {applying ? "Regenerando informe…" : "Aplicar revisión y regenerar informe"}
          </button>
        </div>

        <div className="stack">
          <span className="side-label" data-testid="revision-counter">Historial · Revisión {rc}</span>
          <ol className="timeline">
            {rc > 0 && <li className="cur"><b>Revisión {rc}</b><small>Informe regenerado con tus cambios</small></li>}
            <li className={rc === 0 ? "cur" : ""}><b>Revisión 0 · Informe inicial</b><small>Borrador y lectura editorial</small></li>
            <li><b>Material subido</b><small>{project.manuscript_filename || "Texto pegado"}</small></li>
          </ol>
          <div className="tip"><strong>Revisiones sin límite</strong> Cada versión regenera el informe respetando tu voz.</div>

          <div className="pay">
            <span style={{ fontWeight: 600 }}>Preparar el libro final</span>
            <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ color: "var(--muted)" }}>Modo {project.mode_label} · pago único</span>
              <b className="num" style={{ fontFamily: "var(--serif)", fontSize: 28, fontWeight: 500 }}>{project.price} €</b>
            </span>
            <button type="button" data-testid="stripe-pay-button" className="btn btn-primary" style={{ width: "100%" }} onClick={startPayment} disabled={paying}>
              {paying ? "Redirigiendo…" : `Pagar ${project.price} € y finalizar`}
            </button>
            <small style={{ color: "var(--muted)", textAlign: "center" }}>Pago seguro con Stripe · IVA incluido</small>
          </div>
        </div>
      </div>
    </div>
  );
}
